<template>
    <div class="container-fluid">
        <h1>{{ $t('grid') }}</h1>

        <div :class="$style.grid">
            <div>
                <h2>{{ $t('data') }}</h2>
                <b-form-group :class="$style.group" :label="$t('screenshots')">
                    <b-input-group size="sm" v-for="(image, index) in images" :key="index" class="mb-2">
                        <b-form-input readonly :value="image.name"></b-form-input>
                        <input type="file" accept="image/*" :ref="`img-${index}`" :class="$style.fileInput" @change="processFile($event, index)">
                        <b-input-group-addon append>
                            <b-button @click="addImage(index)" size="sm">
                                <font-awesome-icon icon="file-upload"></font-awesome-icon>
                            </b-button>
                            <b-button @click="removeImage(index)" size="sm">
                                <font-awesome-icon icon="trash"></font-awesome-icon>
                            </b-button>
                        </b-input-group-addon>
                    </b-input-group>

                    <b-button @click="newImage" size="sm">{{ $t('add_image') }}</b-button>
                </b-form-group>

                <b-form-group :class="$style.group" :label="$t('screenshot_signalbooster')">
                    <input type="file" accept="image/*" ref="img-coords" :class="$style.fileInput" @change="processFile($event, 'coords')">

                    <b-input-group size="sm">
                        <b-input-group-text slot="prepend">
                            <font-awesome-icon icon="crop"></font-awesome-icon>
                        </b-input-group-text>

                        <b-form-input readonly :value="this.coordsImg.name"></b-form-input>
                        <b-input-group-addon append>
                            <b-button @click="addImage('coords')" size="sm">
                                <font-awesome-icon icon="file-upload"></font-awesome-icon>
                            </b-button>
                            <b-button @click="removeImage('coords')" size="sm">
                                <font-awesome-icon icon="trash"></font-awesome-icon>
                            </b-button>
                        </b-input-group-addon>
                    </b-input-group>

                    <b-form-text>{{ $t('signalbooster_description') }}</b-form-text>
                </b-form-group>

                <b-form-group label-for="InputGalaxy" :label="$t('galaxy')">
                    <b-form-select v-model="galaxy" :options="galaxies" size="sm"></b-form-select>
                </b-form-group>

                <b-form-group>
                    <b-form-checkbox v-model="firstWave">First Wave</b-form-checkbox>
                </b-form-group>

                <b-form-group :label="$t('coordinates')" style="width: auto;">
                    <b-input-group>
                        <b-form-input
                            v-model.trim="coords"
                            size="sm"
                            maxlength="19"
                            :formatter="onlyHex"/>
                        <bButton
                        slot="append"
                            size="sm"
                            v-if="isTouch"
                            @click="displayHexKeys = !displayHexKeys"
                            >
                            <font-awesome-icon icon="keyboard"></font-awesome-icon>
                        </bButton>
                    </b-input-group>
                    <b-form-text id="inputLiveHelp">
                        {{ $t('enter_last_four_blocks') }}
                        <span style="white-space: nowrap;">(<span :class="$style.bright"><span :class="$style.nope">XXXX :</span>
                        <span :class="$style.green">&check;&check;&check;&check;</span> <span :class="$style.nope"> : </span>
                        <span :class="$style.green">&check;&check;&check;&check;</span> <span :class="$style.nope"> : </span>
                        <span :class="$style.green">&check;&check;&check;&check;</span> <span :class="$style.nope"> : </span>
                        <span :class="$style.green">&check;&check;&check;&check;</span></span>)</span>
                    </b-form-text>
                </b-form-group>
            </div>

            <div>
                <h2>{{ $t('result') }}</h2>
                <p v-if="!hasData">{{ $t('no_data_set') }}</p>
                <canvas v-show="hasData" ref="theGrid" width="100" height="100" :class="$style.canvas"></canvas><br>
                <p>
                    <a v-show="hasData && hasMsBlob" class="btn btn-secondary btn-sm mr-3" role="button" ref="image1" @click="edgeSave">{{ $t('save_glyphs_image') }}</a>
                    <a v-show="hasData && !hasMsBlob" class="btn btn-secondary btn-sm mr-3" role="button" ref="image2" :download="`${dashedCoords}_${glyphs}.png`">{{ $t('save_glyphs_image') }}</a>
                </p>
            </div>

            <p :class="$style.fontLoader">fontLoader</p>
        </div>
    </div>
</template>

<script>
import bButton from 'bootstrap-vue/es/components/button/button';
import bFormCheckbox from 'bootstrap-vue/es/components/form-checkbox/form-checkbox';
import bFormGroup from 'bootstrap-vue/es/components/form-group/form-group';
import bFormInput from 'bootstrap-vue/es/components/form-input/form-input';
import bFormSelect from 'bootstrap-vue/es/components/form-select/form-select';
import bInputGroup from 'bootstrap-vue/es/components/input-group/input-group';
import bInputGroupAddon from 'bootstrap-vue/es/components/input-group/input-group-addon';
import bInputGroupText from 'bootstrap-vue/es/components/input-group/input-group-text';
import bFormText from 'bootstrap-vue/es/components/form/form-text';
import CoordsToGlyphs from '@tornography/nms-glyphs';
import utils from '@/mixins/utils';
import FileSaver from 'file-saver';
import 'mdn-canvas-to-blob';

export default {
    components: {
        bButton,
        bFormCheckbox,
        bFormGroup,
        bFormInput,
        bFormSelect,
        bInputGroup,
        bInputGroupAddon,
        bInputGroupText,
        bFormText
    },
    mixins: [
        utils
    ],
    data () {
        return {
            images: [false],
            coordsImg: false,
            canvas: {
                padding: 5,
                width: 500
            },
            firstWave: false,
            coords: '',
            glyphs: '',
            galaxy: [],
            galaxies: [
                { value: 'Euclid', text: '1 Euclid' },
                { value: 'Hilbert Dimension', text: '2 Hilbert Dimension' },
                { value: 'Calypso', text: '3 Calypso' },
                { value: 'Hesperius Dimension', text: '4 Hesperius Dimension' },
                { value: 'Hyades', text: '5 Hyades' },
                { value: 'Ickjamatew', text: '6 Ickjamatew' },
                { value: 'Budullangr', text: '7 Budullangr' },
                { value: 'Kikolgallr', text: '8 Kikolgallr' },
                { value: 'Eltiensleen', text: '9 Eltiensleen' },
                { value: 'Eissentam', text: '10 Eissentam' },
                { value: 'Elkupalos', text: '11 Elkupalos' },
                { value: 'Aptarkaba', text: '12 Aptarkaba' },
                { value: 'Ontiniangp', text: '13 Ontiniangp' },
                { value: 'Odiwagiri', text: '14 Odiwagiri' },
                { value: 'Ogtialabi', text: '15 Ogtialabi' },
                { value: 'Muhacksonto', text: '16 Muhacksonto' }
            ]
        };
    },
    computed: {
        hasData () {
            if (this.images.some(e => e !== false) || this.coordsImg || this.galaxy.length || this.firstWave || !CoordsToGlyphs(this.coords).error) {
                return true;
            }

            return false;
        },
        dashedCoords () {
            if (!CoordsToGlyphs(this.coords).error) {
                return CoordsToGlyphs(this.coords).coordinates.dashed;
            }
            return 'grid_(no_coordinates).com';
        },
        hasMsBlob () {
            const canvas = this.$refs.theGrid;
            if (canvas.msToBlob) {
                return true;
            }
            return false;
        },
        glyphData () {
            return CoordsToGlyphs(this.coords);
        }
    },
    methods: {
        newImage () {
            this.images.push(false);
        },
        processFile (event, i) {
            const _self = this;
            const fr = new FileReader();

            fr.onload = (file) => {
                const image = new Image();

                image.onload = () => {
                    image.ratio = image.width / image.height;
                    image.newWidth = _self.canvas.width - _self.canvas.padding * 2;
                    image.newHeight = image.newWidth / image.ratio;

                    if (i === 'coords') {
                        this.coordsImg = image;
                    } else {
                        this.$set(this.images, i, image);
                    }
                    this.draw();
                };

                image.src = file.target.result;
                image.name = event.target.files[0].name;
            };

            fr.readAsDataURL(event.target.files[0]);
        },
        addImage (i) {
            let ref;
            if (i === 'coords') {
                ref = 'img-coords';
                this.$refs[ref].click();
            } else {
                ref = `img-${i}`;
                this.$refs[ref][0].click();
            }
        },
        removeImage (i) {
            if (i === 'coords') {
                this.coordsImg = false;
            } else {
                this.images.splice(i, 1);
            }
            this.draw();
        },
        draw () {
            const canvas = this.$refs.theGrid;
            if (canvas.getContext) {
                const ctx = canvas.getContext('2d');
                let start = this.canvas.padding;
                canvas.width = this.canvas.width;
                canvas.height = this.canvas.padding;

                // adding height of images
                this.images.forEach(img => {
                    if (img !== false) {
                        canvas.height = canvas.height + img.newHeight + this.canvas.padding;
                    }
                });

                if (this.coordsImg) {
                    const sWidth = this.coordsImg.width * 0.5;
                    const sHeight = this.coordsImg.height * 0.2;
                    const ratio = sWidth / sHeight;
                    const adjustment = 1;

                    canvas.height = canvas.height + Math.floor(canvas.width / ratio) + this.canvas.padding - adjustment;
                }

                if (this.coords && !CoordsToGlyphs(this.coords).error) {
                    canvas.height = canvas.height + (60 + this.canvas.padding) * 2;
                }

                if (this.galaxy.length || this.firstWave) {
                    canvas.height = canvas.height + 60 + this.canvas.padding;
                }

                // fill background
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // draw images
                this.images.forEach(img => {
                    if (img !== false) {
                        ctx.drawImage(img, this.canvas.padding, start, img.newWidth, img.newHeight);
                        start = start + img.newHeight + this.canvas.padding;
                    }
                });

                // crop and draw signal
                if (this.coordsImg) {
                    const sx = 0;
                    const sy = this.coordsImg.height * 0.8;
                    const sWidth = this.coordsImg.width * 0.5;
                    const sHeight = this.coordsImg.height * 0.2;
                    const ratio = sWidth / sHeight;

                    const dx = this.canvas.padding;
                    const dy = start;
                    const dWidth = canvas.width - this.canvas.padding * 2;
                    const dHeight = Math.floor(dWidth / ratio);

                    ctx.drawImage(this.coordsImg, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
                    start = start + dHeight + this.canvas.padding;
                }

                // draw infos
                if (this.galaxy.length || this.firstWave) {
                    ctx.fillStyle = '#1E2F36';
                    ctx.fillRect(this.canvas.padding, start, canvas.width - this.canvas.padding * 2, 60);
                    const infos = [];
                    if (this.galaxy.length) {
                        infos.push(this.galaxy);
                    }
                    if (this.firstWave) {
                        infos.push('First Wave');
                    }

                    ctx.font = `30px "geo"`;
                    const text = infos.join(' // ').toUpperCase();
                    const metrics = ctx.measureText(text);

                    ctx.font = `30px "geo"`;
                    ctx.textBaseline = 'bottom';
                    ctx.fillStyle = 'white';
                    ctx.fillText(text, (canvas.width - metrics.width) / 2, start + 44);

                    start = start + 60 + this.canvas.padding;
                }

                // draw coords and glpyhs
                if (this.coords && !CoordsToGlyphs(this.coords).error) {
                    this.glyphs = CoordsToGlyphs(this.coords).glyphs.hex;

                    // coords
                    ctx.fillStyle = '#1E2F36';
                    ctx.fillRect(this.canvas.padding, start, canvas.width - this.canvas.padding * 2, 60);

                    const text1 = CoordsToGlyphs(this.coords).coordinates.original.toUpperCase();
                    ctx.font = `30px "geo"`;
                    const metrics1 = ctx.measureText(text1);

                    ctx.font = `30px "geo"`;
                    ctx.textBaseline = 'bottom';
                    ctx.fillStyle = 'white';
                    ctx.fillText(text1, (canvas.width - metrics1.width) / 2, start + 44);

                    start = start + 60 + this.canvas.padding;

                    // glyphs
                    ctx.fillStyle = '#1E2F36';
                    ctx.fillRect(this.canvas.padding, start, canvas.width - this.canvas.padding * 2, 60);

                    const text2 = CoordsToGlyphs(this.coords).glyphs.hex.split('').join(String.fromCharCode(8202));
                    ctx.font = `46px "glyphs-tight"`;
                    const metrics2 = ctx.measureText(text2);

                    ctx.font = `46px "glyphs-tight"`;
                    ctx.textBaseline = 'bottom';
                    ctx.fillStyle = 'white';
                    ctx.fillText(text2, (canvas.width - metrics2.width) / 2, start + 56);

                    start = start + 60 + this.canvas.padding;
                }

                // download button
                const image = canvas.toDataURL('image/png');
                image.replace(/^data:image\/[^;]*/, 'data:application/octet-stream');
                this.$refs.image1.href = image;
                this.$refs.image2.href = image;
            }
        },
        edgeSave () {
            const canvas = this.$refs.theGrid;
            canvas.toBlob(blob => {
                FileSaver.saveAs(blob, `${this.dashedCoords}_${this.glyphs}.png`);
            });
        }
    },
    watch: {
        galaxy (valueNew, valueOld) {
            this.draw();
        },
        firstWave (valueNew, valueOld) {
            this.draw();
        },
        coords (valueNew, valueOld) {
            this.draw();
        }
    }
};
</script>

<style module lang="scss">
    .grid {
        @media( min-width: 900px ) {
            display: grid;
            grid-template-columns: 1fr 500px;
            grid-gap: 2em;
        }

    }

    .fileInput {
        display: none;
    }

    .canvas {
        max-width: 100%;
    }

    .fontLoader {
        font-family: "glyphs-tight";
        visibility: hidden;
        height: 0;
        overflow: hidden;
    }

    .nope {
        color: red;
    }

    .green {
        color: lightgreen;
    }
</style>
